"use client";

import { BarChart } from "@/components/tool-kit/charts/bar-chart";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber } from "@/components/tools/tool-ui";
import type { Strings } from "@/components/tools/unemployment-rate-calculator";
import { t } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type LaborStats,
  pointChange,
  type Rest,
} from "@/lib/tool-kit/econ-macro/labor";

interface Period {
  name: string;
  stats: LaborStats;
}

const clean = (value: number) => (value === 0 ? 0 : value);

export function LaborResults({
  periods,
  rest,
  unitName,
  strings,
}: {
  periods: Period[];
  rest: Rest;
  unitName: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const count = (value: number) =>
    fmt(clean(value), { maximumFractionDigits: 4 });
  const rate = (value: number) =>
    `${fmt(clean(value) * 100, { maximumFractionDigits: 2 })}%`;
  const latest = periods.at(-1);
  const earlier = periods.length > 1 ? periods[0] : null;
  if (!latest) {
    return null;
  }
  const s = latest.stats;
  const change = (pick: (stats: LaborStats) => number) =>
    earlier
      ? t(strings.pointsSuffix, {
          value: fmt(pointChange(pick(earlier.stats), pick(s)), {
            maximumFractionDigits: 2,
            signDisplay: "exceptZero",
          }),
        })
      : null;
  const countChange = (pick: (stats: LaborStats) => number) =>
    earlier
      ? fmt(pick(s) - pick(earlier.stats), {
          maximumFractionDigits: 4,
          signDisplay: "exceptZero",
        })
      : null;
  const measures: {
    label: string;
    show: (stats: LaborStats) => string;
    delta: string | null;
  }[] = [
    {
      label: strings.employed,
      show: (x) => count(x.employed),
      delta: countChange((x) => x.employed),
    },
    {
      label: strings.unemployed,
      show: (x) => count(x.unemployed),
      delta: countChange((x) => x.unemployed),
    },
    {
      label: strings.laborForce,
      show: (x) => count(x.laborForce),
      delta: countChange((x) => x.laborForce),
    },
    {
      label: strings.notInLaborForce,
      show: (x) => count(x.notInLaborForce),
      delta: countChange((x) => x.notInLaborForce),
    },
    {
      label: strings.populationShort,
      show: (x) => count(x.population),
      delta: countChange((x) => x.population),
    },
    {
      label: strings.unemploymentRate,
      show: (x) => rate(x.unemploymentRate),
      delta: change((x) => x.unemploymentRate),
    },
    {
      label: strings.participationRate,
      show: (x) => rate(x.participationRate),
      delta: change((x) => x.participationRate),
    },
    {
      label: strings.employmentRatio,
      show: (x) => rate(x.employmentPopulationRatio),
      delta: change((x) => x.employmentPopulationRatio),
    },
  ];
  const names = [
    strings.seriesEmployed,
    strings.seriesUnemployed,
    strings.seriesNotInLaborForce,
  ];
  const categories = periods.map((p) => ({
    label: p.name,
    values: [p.stats.employed, p.stats.unemployed, p.stats.notInLaborForce],
  }));
  const line = "font-mono text-muted-foreground text-sm leading-relaxed";
  return (
    <div className="space-y-5">
      <StatGrid>
        <StatCard
          emphasis
          label={strings.unemploymentRate}
          value={rate(s.unemploymentRate)}
        />
        <StatCard
          label={strings.participationRate}
          value={rate(s.participationRate)}
        />
        <StatCard
          label={strings.employmentRatio}
          value={rate(s.employmentPopulationRatio)}
        />
      </StatGrid>
      <h3 className={WIDGET_H3}>{strings.measuresHeading}</h3>
      <DataTable
        head={[
          strings.measureColumn,
          ...periods.map((p) => p.name),
          ...(earlier ? [strings.changeColumn] : []),
        ]}
        rows={measures.map((m) => [
          m.label,
          ...periods.map((p) => m.show(p.stats)),
          ...(earlier ? [m.delta ?? ""] : []),
        ])}
      />
      <p className="text-muted-foreground text-sm">
        {t(strings.unitNote, { unit: unitName })}
      </p>
      {periods.map((p) => {
        const x = p.stats;
        const values = {
          e: count(x.employed),
          u: count(x.unemployed),
          lf: count(x.laborForce),
          nilf: count(x.notInLaborForce),
          pop: count(x.population),
          ur: rate(x.unemploymentRate),
          lfpr: rate(x.participationRate),
          epop: rate(x.employmentPopulationRatio),
        };
        return (
          <div className={`${CARD} space-y-1`} key={p.name}>
            <h3 className={WIDGET_H3}>
              {t(strings.workingHeading, { period: p.name })}
            </h3>
            <p className={line}>{t(strings.workLaborForce, values)}</p>
            <p className={line}>
              {t(
                rest === "population"
                  ? strings.workRest
                  : strings.workPopulation,
                values
              )}
            </p>
            <p className={line}>{t(strings.workUnemployment, values)}</p>
            <p className={line}>{t(strings.workParticipation, values)}</p>
            <p className={line}>{t(strings.workEmployment, values)}</p>
          </div>
        );
      })}
      <ChartFigure
        apa={false}
        data={toTsv([
          [strings.chartYTitle, ...names],
          ...categories.map((c) => [c.label, ...c.values]),
        ])}
        figureNumber="1"
        filename={chartFilename(strings.chartTitle, "labor-force")}
        labels={strings}
        note=""
        table={{
          head: [strings.chartYTitle, ...names],
          rows: categories.map((c) => [c.label, ...c.values.map(count)]),
        }}
        title={strings.chartTitle}
      >
        <BarChart
          categories={categories}
          description={t(strings.chartDescription, {
            count: fmt(periods.length),
          })}
          fmt={fmt}
          id="ur-chart"
          label={strings.chartTitle}
          mode="stacked"
          names={names}
          orientation="horizontal"
          style={{
            title: strings.chartTitle,
            xTitle: strings.chartXTitle,
            yTitle: strings.chartYTitle,
            color: "blue",
            valueLabels: true,
            apa: false,
            figureNumber: "1",
            note: "",
          }}
        />
      </ChartFigure>
      <Notice>{strings.figureNote}</Notice>
      <p className="text-muted-foreground text-sm">{strings.definitions}</p>
    </div>
  );
}
